/**
 * @summary - Component that Provides Item Details List functionality
 * @autor vengateshwaran k
 * @since 2024-06-21
 */

import { Box, Button, Stack } from '@mui/material';
import axios from "axios";
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { DefaultCard } from '../../assets/styled-components/card';
import PageTittle from '../../components/commoncomponents/PageTitle';
import SectionTitle from '../../components/commoncomponents/SectionTitle';
import { setHeaderName, setSelectedIndex } from "../../redux/actions/userActions";
import { BemsGraph, FemsGraph, MasterGraph } from '../../utility/apiURLS';
import { getAuthToken, getFacilityId } from "../../utility/tokenUtility";
import CreateNewButton from '../commoncomponents/CreateNewButton';
import CustomAgGrid from '../commoncomponents/CustomAgGrid';
import CustomDialog from '../commoncomponents/CustomDialog';
import RefreshExportButtons from '../commoncomponents/RefreshExportButtons';
import itemDetailsRepository from '../../api/repositories/itemDetailsRepository';
import { useAccess } from "../../AccessContext";
import utilities from '../../utility/utilities';

const ItemLists = () => {
    const dispatch = useDispatch();
    const [rows, setRows] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState(null);
    const navigate = useNavigate();
    const [gridApi, setGridApi] = useState({});
    const [errorMessage, setErrorMessage] = useState("");
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const facilityId = getFacilityId();
    const [filterModel, setFilterModel] = useState([]);
    const [sortModel, setSortModel] = useState([]);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const [exportData, setExportData] = useState([]);
    const [exportCount, setExportCount] = useState(0)

    const accessData = useAccess();
    const access = utilities.getScreenPermissions(accessData, "IMSM005");


    const columns = [
        { field: 'itemcode', headerName: 'Item Code', width: 180 },
        { field: 'itemDescriptionname', headerName: 'Item Description', width: 150 },
        { field: 'itemTypeName', headerName: 'Item Type', width: 180 },
        { field: 'compitembrand', headerName: 'Brand/Make', width: 150, },
        { field: 'status', headerName: 'Status', width: 150, },

    ];

    useEffect(() => {

        fetchItemDetails();
    }, [filterModel, sortModel, pageIndex, pageSize]);

    const fetchItemDetails = async () => {
        try {
            const formattedData = await itemDetailsRepository.getItemDetailsList(facilityId,filterModel,sortModel,pageIndex,pageSize);

            setRows(formattedData?.data.items);
            setRowCount(formattedData?.data.totalCount);
      setExportCount(formattedData?.data.totalCount)
        } catch (error) {
            console.error("Error fetching data", error);
        }
    };

    const fetchData = async () => {
        try {
            const formattedData = await itemDetailsRepository.fetchExportdata(facilityId,filterModel,sortModel);

            setExportData(formattedData.data)
        } catch (error) {
            console.error("Error fetching data", error);
        }
    };

    const handleDelete = (row) => {
        setRowToDelete(row);
        setDeleteDialogOpen(true);
    };

    const handleSuccessDialogClose = () => {
        setSuccessDialogOpen(false);
    };

    const confirmDelete = async () => {

        try {

            if (!rowToDelete || !rowToDelete.itemcodeId) {
                console.error("Row to delete or its id is undefined");
                return;
            } let response = await itemDetailsRepository.removeItemDetails(rowToDelete?.itemcodeId);
            if (response.status === 204) {
                setSuccessDialogOpen(true);
            }
            fetchItemDetails();
        } catch (error) {
            console.error("Error deleting Item Details:", error); // Log the full error object
            if (error.response) {
                console.error("Error response data:", error.response.data); // Log the error response data
                console.error("Error response status:", error.response.status); // Log the error response status
            }
            if (error.response && error.response.status === 400) {
                setErrorMessage("An error occurred while deleting the Item Details. Please try again.");
            } else {
                setErrorMessage("An error occurred while deleting the Item Details. Please try again.");
            }
        } finally {
            setDeleteDialogOpen(false);
            setRowToDelete(null);
        }
    };

    const handleCreateItemDetails = () => {
        navigate("register", {
            state: {
                facilityId: facilityId,
                itemLists: rows
            }
        }
        );
    };

    const handleEdit = (rowData) => {
        navigate("register", {
            state: {
                ...rowData, facilityId: facilityId, isEditMode: true, itemCode: rowData.itemcode, itemDescription: rowData.itemDescriptionname,
                itemTypeId: rowData.itemTypeId, service: rowData?.itemservice, workGroup: rowData?.workgroup, status: rowData?.status,
                store: rowData?.store, brand: rowData?.brand, minimumStock: rowData?.minimumstock, maximumStock: rowData?.maximumstock,
                unitCost: rowData?.unitcost, workGroup: rowData?.workgroup, itemLists: rows
            }
        });

    };

    const handleView = (rowData) => {
        navigate("register", {
            state: {
                ...rowData, facilityId: facilityId, isViewMode: true, itemCode: rowData.itemcode, itemDescription: rowData.itemDescriptionname,
                itemTypeId: rowData.itemTypeId, service: rowData?.itemservice, workGroup: rowData?.workgroup, status: rowData?.status,
                store: rowData?.store, brand: rowData?.brand, minimumStock: rowData?.minimumstock, maximumStock: rowData?.maximumstock,
                unitCost: rowData?.unitcost, itemLists: rows
            }
        })
    };

    const handlebackClick = () => {
        navigate("/dashboard");
        dispatch(setSelectedIndex('SET_INDEX_LABEL', ''));
        dispatch(setHeaderName('SET_HEADER_NAME', 'Dashboard'));
    };

    const handleRefresh = () => {
        setPageIndex(1);
        setPageSize(10);
        setFilterModel([])
        setSortModel([])
      };
    

    return (
        <>
            <Box mt={{ xs: 1, sm: 2 }}>

                <PageTittle title={" Item Details"} />

                <DefaultCard>
                    <Box
                        mb={2}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-end",
                        }}
                    >
                        <Stack direction={"row"}>
                            <SectionTitle title={"Item Details List"} />
                        </Stack>
                        <Stack direction={"row"} spacing={1.5} marginBottom={1}>

                            {access?.canCreate && <CreateNewButton onClick={handleCreateItemDetails} />}

                            <RefreshExportButtons
                                exportFunction={fetchData}
                                exportData={exportData}
                                refData={gridApi}
                                handleRefresh={handleRefresh}
                                title={"Item Details"}                             
                            />
                        </Stack>
                    </Box>
                    <CustomAgGrid
                        rows={rows}
                        columns={columns}
                        handleEdit={access?.canEdit && handleEdit}
                        handleDelete={access?.canDelete && handleDelete}
                        handleView={access?.canView && handleView}
                        ref={gridApi}
                        setGridApi={setGridApi}
                        pagination={false}
                        setFilterModel={setFilterModel}
                        setSortModel={setSortModel}
                        totalCount={rowCount}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        setPageIndex={setPageIndex}
                        setPageSize={setPageSize}

                    />
                </DefaultCard>
            </Box>
            <CustomDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={confirmDelete}
                content="Are you sure you want to delete?"
                cancelBtn='No'
                confirmBtn={'Yes'}
            />
            <CustomDialog
                open={successDialogOpen}
                onClose={handleSuccessDialogClose}
                content="Record deleted successfully."
                confirmBtn="Ok"
                onConfirm={handleSuccessDialogClose}
            />
            <CustomDialog
                open={!!errorMessage}
                onClose={() => setErrorMessage("")}
                onConfirm={() => setErrorMessage("")}
                title="Warning!"
                content={errorMessage}
                cancelBtn="Cancel"
                confirmBtn={'Ok'}
            />
            <Stack my={3} direction={"row"} justifyContent={"end"} spacing={1.5}>
                <Button variant="outlined" onClick={handlebackClick}>
                    Back
                </Button>
            </Stack>
        </>
    );
};

export default ItemLists;
